// ManufacturerDataHealthSection.tsx
//
// "Data Health" section on the manufacturer detail (edit mode only). Diffs the
// hand-curated brand allowlist against the ref-derived set, surfaces exclusions,
// and shows suggested primary-brand / isGeneric classifications so a human can
// eyeball derivation quality.
//
// Phase 3b adds inline WRITE actions — promote / exclude / demote / restore a
// brand, set the primary, and toggle isGeneric — each an immediate single-field
// Firestore write (arrayUnion / arrayRemove / field set) with optimistic UI.
// Destructive actions (demote, isGeneric toggle) confirm first. Optimistic edits
// bubble up via `onManufacturerChange` so the list / overview stay in sync.

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { MovementManufacturer } from '../../types';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import {
  demoteManufacturerBrand,
  excludeManufacturerBrand,
  promoteManufacturerBrand,
  setManufacturerIsGeneric,
  setManufacturerPrimaryBrand,
  unexcludeManufacturerBrand,
} from '../../services/firebase';
import {
  BrandChip,
  brandLabel,
  ChipButton,
  ChipLegend,
  useWatchBrandsMap,
} from './dataHealthShared';
import { ManufacturerBrandDrillDown } from './ManufacturerBrandDrillDown';
import {
  computeManufacturerDataHealth,
  type GenericClassification,
} from './manufacturerDataHealth';

const CLASSIFICATION_LABEL: Record<GenericClassification, string> = {
  generic: 'Independent movement maker',
  'semi-generic': 'Semi-independent (in-house)',
  'non-generic': 'In-house',
};

/**
 * Top-N cap for the collapsed "Derived from refs" list shown for generic
 * manufacturers — a generic maker can derive 100+ brands, so we show the first N
 * alphabetically with a "Show all" toggle rather than a wall of chips.
 */
const GENERIC_DERIVED_PREVIEW = 10;

function currentGenericLabel(v: boolean | undefined): string {
  if (v === undefined) return 'missing';
  return v ? 'Independent movement maker' : 'In-house';
}

/** a minus b (set diff), sorted-unique; b defaults to []. */
function addTo(arr: string[] | undefined, v: string): string[] {
  return Array.from(new Set([...(arr ?? []), v])).sort();
}
function removeFrom(arr: string[] | undefined, v: string): string[] {
  return (arr ?? []).filter((x) => x !== v);
}

function LabeledBlock({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5 mb-1">{hint}</p>}
      <div className={hint ? '' : 'mt-1'}>{children}</div>
    </div>
  );
}

interface ConfirmState {
  title: string;
  body: string;
  confirmLabel: string;
  tone: 'danger' | 'primary';
  onConfirm: () => void;
}

export function ManufacturerDataHealthSection({
  manufacturer,
  onManufacturerChange,
}: {
  manufacturer: MovementManufacturer;
  /** Bubbles optimistic (and reverted) edits up so the list / overview stay fresh. */
  onManufacturerChange?: (updated: MovementManufacturer) => void;
}) {
  const [open, setOpen] = useState(false);
  const { brandsById, loading } = useWatchBrandsMap();

  // Optimistic local copy — resynced whenever the prop changes.
  const [local, setLocal] = useState(manufacturer);
  useEffect(() => setLocal(manufacturer), [manufacturer]);

  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [showAllDerived, setShowAllDerived] = useState(false);
  // Brand whose contributing-refs drill-down is open (null = closed).
  const [drillBrandId, setDrillBrandId] = useState<string | null>(null);

  const health = useMemo(() => computeManufacturerDataHealth(local), [local]);
  const label = (id: string) => brandLabel(brandsById, id);
  const manufacturerName = local.displayName || local.name || local.id;
  const hasDiffs = health.diffCount > 0;
  // For generic manufacturers the per-brand curation UI (promote/exclude, primary
  // picker, full derived wall) isn't useful — a generic maker serves many brands
  // and you won't hand-curate 100+ links. Collapse to a status readout instead.
  const isGeneric = health.suggestedIsGeneric;

  // Optimistically apply `next`, persist via `persist`, revert on failure.
  const run = async (
    next: MovementManufacturer,
    persist: () => Promise<void>,
    successMsg: string,
  ) => {
    const prev = local;
    setLocal(next);
    onManufacturerChange?.(next);
    setBusy(true);
    try {
      await persist();
      toast.success(successMsg);
    } catch (err) {
      console.error('[DataHealth] quick action failed', err);
      toast.error(err instanceof Error ? err.message : 'Update failed');
      setLocal(prev);
      onManufacturerChange?.(prev);
    } finally {
      setBusy(false);
    }
  };

  const promote = (brandId: string) =>
    run(
      { ...local, brandIdsManualInclude: addTo(local.brandIdsManualInclude, brandId) },
      () => promoteManufacturerBrand(local.id, brandId),
      `Promoted ${label(brandId)} to manual list`,
    );

  const exclude = (brandId: string) =>
    run(
      { ...local, brandExclusions: addTo(local.brandExclusions, brandId) },
      () => excludeManufacturerBrand(local.id, brandId),
      `Excluded ${label(brandId)}`,
    );

  const unexclude = (brandId: string) =>
    run(
      { ...local, brandExclusions: removeFrom(local.brandExclusions, brandId) },
      () => unexcludeManufacturerBrand(local.id, brandId),
      `Restored ${label(brandId)}`,
    );

  const demote = (brandId: string) =>
    run(
      { ...local, brandIdsManualInclude: removeFrom(local.brandIdsManualInclude, brandId) },
      () => demoteManufacturerBrand(local.id, brandId),
      `Removed ${label(brandId)} from manual list`,
    );

  const setPrimary = (brandId: string) =>
    run(
      { ...local, primaryBrandId: brandId },
      () => setManufacturerPrimaryBrand(local.id, brandId),
      `Set primary brand to ${label(brandId)}`,
    );

  // Clear the primary brand. `setManufacturerPrimaryBrand(id, '')` deleteField()s
  // it; optimistically '' reads as unset via the health compute's `?.trim() ||
  // undefined`.
  const clearPrimary = () =>
    run(
      { ...local, primaryBrandId: '' },
      () => setManufacturerPrimaryBrand(local.id, ''),
      'Cleared primary brand',
    );

  const setGeneric = (value: boolean) =>
    run(
      { ...local, isGeneric: value },
      () => setManufacturerIsGeneric(local.id, value),
      value ? 'Marked independent movement maker' : 'Marked in-house',
    );

  // --- Confirm-gated destructive actions -----------------------------------
  const confirmDemote = (brandId: string) =>
    setConfirm({
      title: 'Remove manual brand link',
      body: `Remove ${label(brandId)} from the manual list? Refs using this manufacturer no longer count this brand as manually-linked.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: () => {
        setConfirm(null);
        void demote(brandId);
      },
    });

  const confirmToggleGeneric = () => {
    const next = !(local.isGeneric === true);
    setConfirm({
      title: next ? 'Mark independent movement maker' : 'Mark in-house',
      body: `This overrides the derivation-based classification. Manufacturer will be treated as ${
        next ? 'an independent movement maker' : 'in-house'
      } regardless of derived brand count.`,
      confirmLabel: next ? 'Mark independent' : 'Mark in-house',
      tone: 'primary',
      onConfirm: () => {
        setConfirm(null);
        void setGeneric(next);
      },
    });
  };

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="text-gray-400 text-xs w-4">{open ? '▼' : '▶'}</span>
        <h3 className="text-sm font-semibold text-gray-900">Data Health</h3>
        {hasDiffs ? (
          <Badge variant="warning">{health.diffCount} diff{health.diffCount === 1 ? '' : 's'}</Badge>
        ) : (
          <Badge variant="success">clean</Badge>
        )}
        <span className="ml-auto text-[11px] text-gray-400">
          {open ? 'Hide' : 'Show'} manual vs derived audit
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-4 rounded-lg border border-gray-200 bg-gray-50/60 p-3">
          <p className="text-[11px] text-gray-500">
            Derived data comes from{' '}
            <code className="font-mono">brandIdsDerivedFromRefs</code> (populated by the{' '}
            <code className="font-mono">deriveManufacturerBrands</code> callable). Actions here
            write immediately — no need to click Save on the form.
          </p>

          {loading ? (
            <div className="text-xs text-gray-500">Loading brands…</div>
          ) : (
            <>
              <ChipLegend
                items={isGeneric ? ['stale', 'excluded'] : ['new', 'stale', 'excluded', 'primary']}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <LabeledBlock title={`Manual brand links (${health.manual.length})`}>
                  {health.manual.length === 0 ? (
                    <span className="text-xs text-gray-400">No manual links</span>
                  ) : (
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {health.manual.map((id) => (
                        <BrandChip
                          key={id}
                          id={id}
                          brandsById={brandsById}
                          tone="manual"
                          onClick={() => setDrillBrandId(id)}
                        />
                      ))}
                    </div>
                  )}
                </LabeledBlock>
                <LabeledBlock title={`Derived from refs (${health.derived.length})`}>
                  {health.derived.length === 0 ? (
                    <span className="text-xs text-gray-400">No derived links</span>
                  ) : isGeneric && !showAllDerived ? (
                    <>
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                        {health.derived.slice(0, GENERIC_DERIVED_PREVIEW).map((id) => (
                          <BrandChip
                            key={id}
                            id={id}
                            brandsById={brandsById}
                            tone="derived"
                            onClick={() => setDrillBrandId(id)}
                          />
                        ))}
                      </div>
                      {health.derived.length > GENERIC_DERIVED_PREVIEW && (
                        <button
                          type="button"
                          onClick={() => setShowAllDerived(true)}
                          className="mt-1.5 text-[11px] font-medium text-brand-600 hover:text-brand-700"
                        >
                          Show all {health.derived.length}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                        {health.derived.map((id) => (
                          <BrandChip
                            key={id}
                            id={id}
                            brandsById={brandsById}
                            tone="derived"
                            onClick={() => setDrillBrandId(id)}
                          />
                        ))}
                      </div>
                      {isGeneric && health.derived.length > GENERIC_DERIVED_PREVIEW && (
                        <button
                          type="button"
                          onClick={() => setShowAllDerived(false)}
                          className="mt-1.5 text-[11px] font-medium text-brand-600 hover:text-brand-700"
                        >
                          Show fewer
                        </button>
                      )}
                    </>
                  )}
                </LabeledBlock>
              </div>

              {!isGeneric && (
              <LabeledBlock
                title={`New in derived — missing from manual (${health.newInDerived.length})`}
                hint="Refs use this manufacturer's calibres for these brands but they were never hand-curated. Promote to add to the manual list, or exclude to dismiss."
              >
                {health.newInDerived.length === 0 ? (
                  <span className="text-xs text-gray-400">
                    None — manual list covers the derived set
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {health.newInDerived.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1">
                        <BrandChip
                          id={id}
                          brandsById={brandsById}
                          tone="new"
                          onClick={() => setDrillBrandId(id)}
                        />
                        <ChipButton
                          tone="positive"
                          disabled={busy}
                          onClick={() => void promote(id)}
                          title={`Add ${label(id)} to the manual list`}
                        >
                          + Promote
                        </ChipButton>
                        <ChipButton
                          tone="danger"
                          disabled={busy}
                          onClick={() => void exclude(id)}
                          title={`Exclude ${label(id)} from the effective set`}
                        >
                          ✕ Exclude
                        </ChipButton>
                      </span>
                    ))}
                  </div>
                )}
              </LabeledBlock>
              )}

              <LabeledBlock
                title={`In manual but not derived (${health.staleInManual.length})`}
                hint="Manually linked, but no refs actually use its calibres for them. Possibly a stale link — demote to remove from the manual list."
              >
                {health.staleInManual.length === 0 ? (
                  <span className="text-xs text-gray-400">
                    None — every manual link is backed by refs
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {health.staleInManual.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1">
                        <BrandChip
                          id={id}
                          brandsById={brandsById}
                          tone="stale"
                          onClick={() => setDrillBrandId(id)}
                        />
                        <ChipButton
                          tone="danger"
                          disabled={busy}
                          onClick={() => confirmDemote(id)}
                          title={`Remove ${label(id)} from the manual list`}
                        >
                          Demote
                        </ChipButton>
                      </span>
                    ))}
                  </div>
                )}
              </LabeledBlock>

              {health.exclusions.length > 0 && (
                <LabeledBlock
                  title={`Excluded brands (${health.exclusions.length})`}
                  hint="Dismissed via brandExclusions — removed from the effective set even if refs imply them. Restore to undo."
                >
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {health.exclusions.map((id) => (
                      <span key={id} className="inline-flex items-center gap-1">
                        <BrandChip
                          id={id}
                          brandsById={brandsById}
                          tone="excluded"
                          onClick={() => setDrillBrandId(id)}
                        />
                        <ChipButton
                          disabled={busy}
                          onClick={() => void unexclude(id)}
                          title={`Restore ${label(id)} to the effective set`}
                        >
                          ↺ Restore
                        </ChipButton>
                      </span>
                    ))}
                  </div>
                </LabeledBlock>
              )}

              {!isGeneric && (
              <LabeledBlock
                title={`Effective brand set (${health.effective.length})`}
                hint="union(manual, derived) − exclusions. Pick which of these is the primary brand."
              >
                {health.effective.length === 0 ? (
                  <span className="text-xs text-gray-400">empty</span>
                ) : (
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {health.effective.map((id) => {
                      const isPrimary = id === health.currentPrimaryBrandId;
                      return (
                        <span key={id} className="inline-flex items-center gap-1">
                          <BrandChip
                            id={id}
                            brandsById={brandsById}
                            tone={isPrimary ? 'primary' : 'manual'}
                            prefix={isPrimary ? '★' : undefined}
                            onClick={() => setDrillBrandId(id)}
                          />
                          {isPrimary ? (
                            <>
                              <Badge variant="success">PRIMARY</Badge>
                              <ChipButton
                                tone="danger"
                                disabled={busy}
                                onClick={() => void clearPrimary()}
                                title="Clear the primary brand"
                              >
                                ✕ Clear
                              </ChipButton>
                            </>
                          ) : (
                            <ChipButton
                              tone="positive"
                              disabled={busy}
                              onClick={() => void setPrimary(id)}
                              title={`Set ${label(id)} as the primary brand`}
                            >
                              ★ Set primary
                            </ChipButton>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
              </LabeledBlock>
              )}

              <div className="grid gap-4 sm:grid-cols-2 border-t border-gray-200 pt-3">
                {!isGeneric && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-gray-700">Primary brand</div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-20 text-gray-500">Current:</span>
                    {health.currentPrimaryBrandId ? (
                      <BrandChip
                        id={health.currentPrimaryBrandId}
                        brandsById={brandsById}
                        tone="primary"
                      />
                    ) : (
                      <span className="text-gray-400">not set</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-20 text-gray-500">Suggested:</span>
                    {health.suggestedPrimaryBrandId ? (
                      <>
                        <BrandChip
                          id={health.suggestedPrimaryBrandId}
                          brandsById={brandsById}
                          tone="primary"
                          prefix="★"
                        />
                        <Badge variant="info">SUGGESTED</Badge>
                        {health.primaryDisagrees && (
                          <ChipButton
                            tone="positive"
                            disabled={busy}
                            onClick={() => void setPrimary(health.suggestedPrimaryBrandId!)}
                            title="Set the suggested brand as primary"
                          >
                            Apply
                          </ChipButton>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">no candidate</span>
                    )}
                  </div>
                  {health.primaryDisagrees && health.suggestedPrimaryBrandId && (
                    <p className="text-[11px] text-amber-700">
                      Suggested primary differs from the stored value.
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400">
                    Suggestion prefers the manufacturer&apos;s own brand, else the stored
                    primary, else the first effective brand (alphabetical) — not a frequency
                    ranking. Hidden for independent movement makers / very-broad manufacturers.
                  </p>
                </div>
                )}

                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-gray-700">
                    Independent movement maker classification
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-20 text-gray-500">Current:</span>
                    <span className="font-medium">
                      {currentGenericLabel(health.currentIsGeneric)}
                    </span>
                    <ChipButton
                      disabled={busy}
                      onClick={confirmToggleGeneric}
                      title="Override the derivation-based classification"
                    >
                      {local.isGeneric === true ? 'Mark in-house' : 'Mark independent'}
                    </ChipButton>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-20 text-gray-500">Suggested:</span>
                    <span className="font-medium">
                      {CLASSIFICATION_LABEL[health.suggestedClassification]}
                    </span>
                    <Badge variant="info">SUGGESTED</Badge>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {health.currentIsGeneric === undefined ? (
                      <>
                        From {health.effective.length} effective brand
                        {health.effective.length === 1 ? '' : 's'} (0 → independent; 1–2 →
                        in-house; 3–8 → semi-independent; 9+ → independent).
                      </>
                    ) : (
                      <>
                        Set by the explicit isGeneric flag ({health.effective.length}{' '}
                        effective brand{health.effective.length === 1 ? '' : 's'}).
                      </>
                    )}
                  </p>
                  {health.isGenericDisagrees && (
                    <p className="text-[11px] text-amber-700">
                      {health.currentIsGeneric === undefined
                        ? 'isGeneric is not set on this manufacturer.'
                        : 'Suggested classification disagrees with the stored isGeneric.'}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.title ?? ''}
        size="sm"
      >
        {confirm && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">{confirm.body}</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant={confirm.tone === 'danger' ? 'danger' : 'primary'}
                onClick={confirm.onConfirm}
              >
                {confirm.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ManufacturerBrandDrillDown
        isOpen={drillBrandId !== null}
        onClose={() => setDrillBrandId(null)}
        manufacturerId={local.id}
        manufacturerName={manufacturerName}
        brandId={drillBrandId ?? ''}
        brandsById={brandsById}
      />
    </div>
  );
}
